import React, { useState } from 'react';
import { SharedChatUI } from './SharedChatUI.js';
import type { ChatMessageViewModel } from '../domain/chat-contracts.js';
import '../style.css';

export interface GenericChannelViewProps {
  channelId: string;
  nodeId?: string;
  displayName?: string;
  broadcast?: any;
  messages?: ChatMessageViewModel[];
  isLoading?: boolean;
  onSend?: (text: string, attachments?: any[]) => void;
  onDelete?: (eventId: string) => void;
  onClearHistory?: () => void;
  onExport?: (channelId?: string, nodeId?: string) => void;
  onStageImport?: (file: File) => void;
  stagedImport?: any;
  onConfirmImport?: () => void;
  onCancelImport?: () => void;
  header?: React.ReactNode;
  className?: string;
  accentColor?: string;
  service?: any;
}

export const GenericChannelView: React.FC<GenericChannelViewProps> = ({
  channelId,
  nodeId = 'local-peer',
  displayName = 'You',
  broadcast,
  messages: propMessages,
  isLoading = false,
  onSend: propOnSend,
  onDelete: propOnDelete,
  onClearHistory: propOnClearHistory,
  onExport,
  onStageImport,
  stagedImport,
  onConfirmImport,
  onCancelImport,
  header: customHeader,
  className,
  accentColor = '#3b82f6',
}) => {
  const [localMessages, setLocalMessages] = useState<ChatMessageViewModel[]>([]);

  const activeMessages = propMessages ?? localMessages;

  const handleSend = (text: string, attachments?: any[]) => {
    if (propOnSend) {
      propOnSend(text, attachments);
    } else {
      const newMsg: ChatMessageViewModel = {
        eventId: `msg-${Date.now()}`,
        channelId,
        authorId: nodeId,
        authorPrincipalId: nodeId,
        authorDisplayName: displayName,
        authorType: 'human',
        body: text,
        createdAt: Date.now(),
        clusterPosition: 'single',
        isMine: true,
        attachments,
      };
      setLocalMessages((prev) => [...prev, newMsg]);
    }
  };

  const handleDelete = (eventId: string) => {
    if (propOnDelete) {
      propOnDelete(eventId);
    } else {
      setLocalMessages((prev) => prev.filter((m) => m.eventId !== eventId));
    }
  };

  const handleClearHistory = () => {
    if (propOnClearHistory) {
      propOnClearHistory();
    } else {
      setLocalMessages([]);
    }
  };

  const header = customHeader || (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>#{channelId}</h2>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>
        Sovereign encrypted mesh channel
      </span>
    </div>
  );

  return (
    <div className={`herx-chat-root ${className || ''}`} style={{ width: '100%', height: '100%' }}>
      <SharedChatUI
        messages={activeMessages}
        composer={isLoading ? 'offline_queued' : 'empty'}
        onSend={handleSend}
        onDelete={handleDelete}
        onClearHistory={handleClearHistory}
        onExport={onExport ? () => onExport(channelId, nodeId) : undefined}
        onStageImport={onStageImport}
        stagedImport={stagedImport}
        onConfirmImport={onConfirmImport}
        onCancelImport={onCancelImport}
        header={header}
        accentColor={accentColor}
        currentPrincipalId={nodeId}
      />
    </div>
  );
};

export default GenericChannelView;
